import { useCallback } from "react";
import {
  SchedulePreviewAppointment,
  type ResourceButtonOnClickProps,
} from "@grow-therapy-team/seedling-components";
import { useOpenClientInfoDrawerForAppointment as baseUseOpenClientInfoDrawerForAppointment } from "../useOpenClientInfoDrawerForAppointment";
import {
  PreHandler,
  useResourceButtonOnClickHandlers,
  type UseResourceButtonOnClickHandlersDependencies,
} from "./useResourceButtonOnClickHandlers";
import { useAtomCallback as baseUseAtomCallback } from "jotai/utils";
import { drawerStateAtom } from "../../state";
import { useGetCurrentProviderQuery as baseUseGetCurrentProviderQuery } from "../../useGetCurrentProviderQuery";

export type UseExternalResourceButtonOnClickHandlersDependencies = {
  useAtomCallback: typeof baseUseAtomCallback;
  useGetCurrentProviderQuery: typeof baseUseGetCurrentProviderQuery;
  useOpenClientInfoDrawerForAppointment: typeof baseUseOpenClientInfoDrawerForAppointment;
  baseHandlerDependencies?: UseResourceButtonOnClickHandlersDependencies;
};

/**
 * Hook that provides onClick handlers for pre-visit summary resource buttons
 * outside of the drawer context, e.g. for PVS in the provider's upcoming
 * appointment schedule in the waiting room. These handlers first open the
 * client info drawer, then navigate to the appropriate tab and focus the
 * relevant card. They rely on the base `useResourceButtonOnClickHandlers` hook
 * for drawer state logic and focus management.
 */
export function useExternalResourceButtonOnClickHandlers(
  appointment: SchedulePreviewAppointment["node"],
  dependencies: UseExternalResourceButtonOnClickHandlersDependencies = {
    useAtomCallback: baseUseAtomCallback,
    useGetCurrentProviderQuery: baseUseGetCurrentProviderQuery,
    useOpenClientInfoDrawerForAppointment:
      baseUseOpenClientInfoDrawerForAppointment,
  },
): ResourceButtonOnClickProps {
  const {
    useAtomCallback,
    useGetCurrentProviderQuery,
    useOpenClientInfoDrawerForAppointment,
    baseHandlerDependencies,
  } = dependencies;
  const getDrawerState = useAtomCallback(
    useCallback((get) => get(drawerStateAtom), []),
  );
  const openClientInfoDrawerForAppointment =
    useOpenClientInfoDrawerForAppointment();
  const { data } = useGetCurrentProviderQuery();

  const openDrawerForAppointment: PreHandler = useCallback(
    ({ setState, scrollAndFocus }) => {
      const wasDrawerOpen = !!getDrawerState();
      openClientInfoDrawerForAppointment(appointment);
      setState();
      // We only scroll and focus the relevant card if the drawer was already
      // open. Otherwise, since opening the drawer constitutes adding additional
      // content to the DOM, we prefer the default focus management behavior of
      // the drawer opening (namely, transferring focus to the close button of
      // the drawer, which gives a stronger clue to a assistive technology user
      // about how the UI has updated).
      if (wasDrawerOpen) scrollAndFocus?.();
    },
    [appointment, getDrawerState, openClientInfoDrawerForAppointment],
  );

  return useResourceButtonOnClickHandlers({
    appointmentShortId: appointment?.appointmentShortId,
    providerShortId: data?.currentProvider?.shortId,
    preHandler: openDrawerForAppointment,
    dependencies: baseHandlerDependencies,
  });
}
